(ns hai.library.sources.create
  "Sources creation page components"
  (:require
   [clojure.string :as str]
   [hai.library.sources.ms-file-picker :as ms-file-picker]
   [hai.library.sources.utils :as sources-utils]
   [hai.ui.components :as c]
   [hai.ui.components.tabs :as tabs]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.ui.toast :as toast]
   [hai.ui.tokens :as t]
   [hai.ui.utils.polling :as polling]
   [hai.web-app.components.form :as form]
   [hai.web-app.layout :as layout]
   [lambdaisland.glogi :as log]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defsub ::source-type
  [db]
  (or (keyword (get-in db [:routing/current-route :path-params :source/type]))
      :file))

(o/defstyled upload-area :div
  :border-2 :rounded-lg :p-10
  :flex :flex-col :items-center :justify-center :gap-3
  :cursor-pointer
  {:border-color t/--property-16
   :background-color t/--property-17
   :transition "all 0.2s ease"}

  [:&:hover
   {:border-color t/--property-18
    :background-color t/--property-19}]

  [:&.dragging
   {:border-color t/--property-20
    :background-color t/--property-21}])

(o/defstyled upload-icon-wrapper :div
  :w-12 :h-12 :rounded-full :flex :items-center :justify-center
  [:>svg {:fill t/--humble-blue}
   :w-full :h-full :p-3]
  {:background-color t/--property-22})

(o/defstyled upload-text :div
  :text-center
  [:.primary :text-base :font-semibold :mb-1]
  [:.secondary :text-sm :text-gray-600]
  [:.choose-file :text-blue-600 :cursor-pointer
   {:&:hover {:text-decoration "underline"}}])

(o/defstyled supported-types :div
  :text-xs :text-gray-500 :text-center :mt-4)


(o/defstyled input-area :div
  :mt-4 :flex :flex-col :gap-3)

(o/defstyled selected-files-list :div
  :mt-4 :flex :flex-col :gap-2)

(o/defstyled file-item :div
  :flex :items-center :justify-between :p-3 :border :rounded
  {:border-color t/--property-16
   :background-color t/--property-17}
  [:.file-info :flex :items-center :gap-2 :flex-1]
  [:.file-name :text-sm :font-medium]
  [:.file-size :text-xs :text-gray-500]
  [:.remove-btn :cursor-pointer :text-red-600
   {:&:hover {:text-decoration "underline"}}])

(defn file-extension
  [filename]
  (-> filename
      (str/split #"\.")
      last
      str/lower-case))

(defn format-file-size
  "Format file size in bytes to human readable format"
  [bytes]
  (cond
    (< bytes 1024) (str bytes " B")
    (< bytes (* 1024 1024)) (str (.toFixed (/ bytes 1024) 2) " KB")
    (< bytes (* 1024 1024 1024)) (str (.toFixed (/ bytes 1024 1024) 2) " MB")
    :else (str (.toFixed (/ bytes 1024 1024 1024) 2) " GB")))

(defn selected-files-view
  [{:keys [files uploading?]}]
  (when (seq files)
    [selected-files-list
     [:div.text-sm.font-medium.mb-2 "Selected files:"]
     (for [[idx file] (map-indexed vector files)]
       ^{:key idx}
       [file-item
        [:div.file-info
         [:div.file-name (.-name file)]
         [:div.file-size (format-file-size (.-size file))]]
        (when-not uploading?
          [c/CalloutRoot {:color "green"}
           [c/CalloutIcon
            [icons/outlined-task_alt]]
           [c/CalloutText "Added"]])])
     (when uploading?
       [:div.text-sm.text-gray-600.mt-2.flex.items-center.gap-2
        [:div.animate-spin "⏳"]
        [:span "Uploading files..."]])]))

;; TODO: These limits are duplicated in hai.storage.file (backend source of truth).
;; Consolidate when hai/storage (or a lighter component) is added to web-app deps.
(def max-file-size-bytes
  (* 50 1024 1024))

(def supported-file-types
  {:pdf {:extensions #{"pdf"}
         :mime-types #{"application/pdf"}
         :binary? true}
   :docx {:extensions #{"docx"}
          :mime-types #{"application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
          :binary? true}
   :pptx {:extensions #{"pptx"}
          :mime-types #{"application/vnd.openxmlformats-officedocument.presentationml.presentation"}
          :binary? true}
   :xlsx {:extensions #{"xlsx"}
          :mime-types #{"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
          :binary? true}
   :csv {:extensions #{"csv"}
         :mime-types #{"text/csv"}
         :binary? false}
   :txt {:extensions #{"txt"}
         :mime-types #{"text/plain"}
         :binary? false}
   :markdown {:extensions #{"md" "markdown"}
              :mime-types #{"text/markdown" "text/plain"}
              :binary? false}})

(defn get-file-type-info
  "Returns the file type info if the file is supported, nil otherwise"
  [file]
  (let [filename (.-name file)
        mime-type (.-type file)
        ext (file-extension filename)]
    (->> supported-file-types
         vals
         (filter (fn [type-info]
                   (or (contains? (:extensions type-info) ext)
                       (contains? (:mime-types type-info) mime-type))))
         first)))

(defn validate-file
  "Validates file type and size. Returns {:valid? bool :error string}"
  [file]
  (let [size (.-size file)
        type-info (get-file-type-info file)]
    (cond
      (nil? type-info)
      {:valid? false
       :error "Unsupported file type. Please upload: PDF, DOCX, PPTX, XLSX, CSV, TXT, or Markdown files"}

      (> size max-file-size-bytes)
      {:valid? false
       :error (str "File is too large. Maximum size is 50 MB. This file is " (format-file-size size) ".")}

      :else
      {:valid? true
       :binary? (:binary? type-info)})))

(defn parse-files!
  "Validate files and store File objects in the selected-files atom."
  [files selected-files-atom]
  (reset! selected-files-atom [])
  (let [validation-errors (atom [])]
    (doseq [file files]
      (let [validation (validate-file file)]
        (if (:valid? validation)
          (swap! selected-files-atom conj file)
          (swap! validation-errors conj {:file (.-name file)
                                         :error (:error validation)}))))
    (when (seq @validation-errors)
      (doseq [{:keys [file error]} @validation-errors]
        (toast/error! {:text (str file ": " error)
                       :timeout 8000}))))
  #_(>e [:routing/navigate [:sources/create {:source/type :file}]]))

(defn file-upload-view
  [{:keys [on-file-select disabled?]}]
  (r/with-let [dragging? (r/atom false)]
    [upload-area
     {:on-click #(when-not disabled?
                   (when-let [input (.getElementById js/document "file-input")]
                     (.click input)))
      :style (when disabled? {:opacity "0.5" :cursor "not-allowed"})
      :on-drag-enter (fn [e]
                       (.preventDefault e)
                       (.stopPropagation e)
                       (when-not disabled?
                         (reset! dragging? true)))
      :on-drag-over (fn [e]
                      (.preventDefault e)
                      (.stopPropagation e))
      :on-drag-leave (fn [e]
                       (.preventDefault e)
                       (.stopPropagation e)
                       (reset! dragging? false))
      :on-drop (fn [e]
                 (.preventDefault e)
                 (.stopPropagation e)
                 (reset! dragging? false)
                 (when-not disabled?
                   (when-let [files (.. e -dataTransfer -files)]
                     (on-file-select #js {:target #js {:files files}}))))
      :class (when @dragging? "dragging")}
     [upload-icon-wrapper
      [icons/outlined-file_upload]]
     [upload-text
      [:div.primary "Upload sources"]
      [:div.secondary
       "Drag and drop or "
       [:span.choose-file "choose file"]
       " to upload"]]
     [supported-types
      "Supported file types: PDF, DOCX, PPTX, XLSX, CSV, TXT, Markdown (.md) files."]
     [:input
      {:id "file-input"
       :type "file"
       :multiple true
       :accept ".pdf,.docx,.pptx,.xlsx,.csv,.txt,.md"
       :style {:display "none"}
       :disabled disabled?
       :on-change on-file-select}]]))

(o/defstyled gdrive-instructions :div
  :flex :flex-col :gap-3 :text-sm :text-gray-700
  [:.step :flex :gap-2]
  [:.step-number :font-medium]
  [:.step-content :flex :flex-wrap :items-center :gap-1]
  [:.email-link :text-blue-600 :cursor-pointer :inline-flex :items-center :gap-1
   [:>svg :w-3 :h-3]
   {:&:hover {:text-decoration "underline"}}]
  [:.gdrive-link :text-blue-600 :inline-flex :items-center :gap-1
   [:>svg :w-3 :h-3]
   {:&:hover {:text-decoration "underline"}}]
  [:.video-link :mt-3 :text-blue-600 :cursor-pointer :inline-flex :items-center :gap-1
   [:>svg :w-4 :h-4]
   {:&:hover {:text-decoration "underline"}}])

(defn gdrive-sharing-info
  []
  (r/with-let [email "humbleassistant@thehumbleai.com"]
    [gdrive-instructions
     [:div.step
      [:span.step-number "1."]
      [:span.step-content
       "Copy "
       [:span.email-link
        {:on-click (fn []
                     (.. js/navigator -clipboard (writeText email))
                     (toast/info! {:text "Email copied to clipboard"
                                   :timeout 2000}))}
        email
        [icons/outlined-content_copy]]
       " and open the file or folder in "
       [:a.gdrive-link
        {:href "https://drive.google.com"
         :target "_blank"
         :rel "noopener noreferrer"}
        "Google Drive"
        [icons/outlined-open_in_new]]
       "."]]
     [:div.step
      [:span.step-number "2."]
      [:span.step-content
       "Select 'Share', paste the assistant email in as a viewer and copy the shareable link."]]]))

(o/defstyled form-content :div
  :flex :flex-col :gap-4)

(o/defstyled description-text :div
  :text-sm :text-gray-700 :mb-6
  [:strong :font-semibold])

(defn build-form-data
  "Build multipart/form-data from source data with binary files.

  Takes source-data map with keys:
  - :ai-source/title - Source title (string)
  - :ai-source/type - Source type keyword (:file, :google-drive, :website)
  - :ai-source/link - Optional link (string)
  - :files - Vector of File objects (native browser File objects)

  Returns js/FormData object suitable for multipart upload. Files are sent as
  binary data without base64 encoding.

  The :fetch effect (hai.web-app.util.fetch) detects FormData and bypasses
  Transit serialization, allowing the browser to handle multipart encoding.

  The backend will receive this as multipart/form-data with:
  - Text fields: ai-source/title, ai-source/type, ai-source/link
  - File array: files[] containing binary file data

  Example source-data:
  {:ai-source/title \"My Documents\"
   :ai-source/type :file
   :files [#js File {...} #js File {...}]}"
  [source-data]
  (let [form-data (js/FormData.)]
    (.append form-data "ai-model-config/uuid" (:ai-model-config/uuid source-data))
    (.append form-data "ai-source/title" (:ai-source/title source-data))
    (.append form-data "ai-source/type" (name (:ai-source/type source-data)))
    (when-let [link (:ai-source/link source-data)]
      (.append form-data "ai-source/link" link))
    (doseq [file (:files source-data)]
      (.append form-data "files" file))
    form-data))

(defevent ::in-flight.set [db val]
  (assoc-in db [::in-flight?] val))

(defsub ::in-flight? [db]
  (::in-flight? db))

(defevent-fx ::create
  [_ source-data]
  {:fetch {:method :post
           :url "/assistant/v0/sources"
           :body (build-form-data source-data)
           :on-success-fx [#_ [:dispatch [:routing/navigate [:sources/table]]]
                           [:dispatch [::in-flight.set false]]
                           [:dispatch [:toast/success {:text "Knowledge uploaded."}]]
                           [:dispatch [::polling/start {:id :sources-table-poll
                                                        :table :hai.library.sources.table/table
                                                        :interval-ms 10000
                                                        :stop-condition-fn sources-utils/all-sources-finished?}]]]}})

(defn source-form
  "Generic source upload component, being used at multiple places."
  [& {:keys [ai-model-config-uuid on-submit]
      :or {on-submit #(>e [::create %])}}]
  (r/with-let [!in-flight? (<s [::in-flight?])
               !beta? (<s :feature-flag/beta-features?)
               !entity (r/atom {})
               !link-ref (r/atom nil)
               !selected-files (r/atom [])]
    (let [source-type (or (.get (js/URLSearchParams. js/document.location.search) "source_tab") "file")]
      [form/form
       [form-content
        [description-text
         "Sources let Humble assistant base its responses on the information that matters most to you."
         [:br]
         "(Examples: marketing plans, course reading, research notes, meeting transcripts, sales documents, etc.)"]

        [tabs/tabs-container
         [c/TabsRoot {:defaultValue source-type}
          [c/TabsList
           [c/TabsTrigger {:value "file"}
            [icons/outlined-file_upload]
            "Upload from computer"]
           [c/TabsTrigger {:value "google-drive"}
            [icons/google-drive-icon]
            "Add from Google Drive"]
           (when @!beta?
             [:<>
              [c/TabsTrigger {:value "sharepoint"}
               [icons/microsoft {:width 20 :height 20}]
               "Add from Sharepoint"]
              [c/TabsTrigger {:value "onedrive"}
               [icons/microsoft {:width 20 :height 20}]
               "Add from OneDrive"]])
           [c/TabsTrigger {:value "website"}
            [icons/outlined-add_link]
            "Link to public websites"]]

          ;; File upload tab content
          [c/TabsContent {:value "file"}
           [file-upload-view
            {:on-file-select (fn [e]
                               (>e [::in-flight.set true])
                               (let [files (array-seq (.. e -target -files))]
                                 (parse-files! files !selected-files)
                                 (on-submit {:ai-source/type :file
                                             :ai-model-config/uuid ai-model-config-uuid
                                             :files @!selected-files})))
             :disabled? @!in-flight?}]
           [selected-files-view
            {:files @!selected-files
             :uploading? @!in-flight?
             :on-remove (fn [idx]
                          (swap! !selected-files
                                 (fn [files]
                                   (vec (concat (subvec files 0 idx)
                                                (subvec files (inc idx)))))))}]]

          ;; Google Drive tab content
          [c/TabsContent {:value "google-drive"}
           [gdrive-sharing-info]
           [input-area
            [c/Flex {:gap "2" :align "center"}
             [c/TextFieldRoot {:style {:width "100%"}
                               :ref (fn [el]
                                      (log/info :link/ref el)
                                      (reset! !link-ref el))
                               :placeholder "Paste Google Drive link here..."
                               :on-change (fn [e]
                                            (swap! !entity assoc :ai-source/link (.. e -target -value)))}
              [c/TextFieldSlot [icons/google-drive-icon]]]
             [c/Button
              {:disabled @!in-flight?
               :on-click (fn [_]
                           (>e [::in-flight.set true])
                           (let [source-data (assoc @!entity
                                                    :ai-model-config/uuid ai-model-config-uuid
                                                    :ai-source/title "Google Drive Source"
                                                    :ai-source/type :google-drive)]
                             (on-submit source-data)
                             (set! (.-value @!link-ref) "")
                             (swap! !entity assoc :ai-source/link "")))
               :size "2"}
              (if @!in-flight? "Uploading..." "Add link")]]
            #_[gdrive-instructions
               [:span.video-link
                {:on-click #(js/window.open "https://www.youtube.com/watch?v=dQw4w9WgXcQ" "_blank")}
                [icons/outlined-play_circle]
                "Play a short video on sharing Google Drive links."]]]]

          (when @!beta?
            [:<>
             [c/TabsContent {:value "sharepoint"}
              [ms-file-picker/picker
               {:type :sharepoint
                :confirm-button-text "Create new Source"
                :on-confirm #(on-submit {:ai-source/type :storage
                                         :ai-model-config/uuid ai-model-config-uuid
                                         :ai-source/link (:storage.entry/self %)
                                         :ai-source/title (:storage.entry/display-name %)})}]]

             [c/TabsContent {:value "onedrive"}
              [ms-file-picker/picker
               {:type :onedrive
                :confirm-button-text "Create new Source"
                :on-confirm #(on-submit {:ai-source/type :storage
                                         :ai-model-config/uuid ai-model-config-uuid
                                         :ai-source/link (:storage.entry/self %)
                                         :ai-source/title (:storage.entry/display-name %)})}]]])

          ;; Website tab content
          [c/TabsContent {:value "website"}
           [c/Text {:weight "bold"} "Enter website link"]
           [input-area
            [c/Flex {:gap "2"}
             [c/TextFieldRoot
              {:style {:width "100%"}
               :ref (fn [el] (reset! !link-ref el))
               :placeholder "Paste website link here..."
               :on-change (fn [e]
                            (swap! !entity assoc :ai-source/link (.. e -target -value)))}
              [c/TextFieldSlot [icons/outlined-add_link]]]
             [c/Button
              {:disabled @!in-flight?
               :on-click (fn [_]
                           (>e [::in-flight.set true])
                           (let [source-data (assoc @!entity
                                                    :ai-model-config/uuid ai-model-config-uuid
                                                    :ai-source/title (:ai-source/link @!entity)
                                                    :ai-source/type :website)]
                             (on-submit source-data)
                             (set! (.-value @!link-ref) "")
                             (swap! !entity assoc :ai-source/link "")))
               :size "2"}
              (if @!in-flight? "Uploading..." "Add link")]]]]]]]])))

(defn page
  [_]
  [layout/page
   [layout/breadcrumbs
    {:breadcrumbs ["Add source"]}]
   [layout/heading {:label "Add source"}]
   [layout/content
    [source-form {:on-submit #(>e [::create %])
                  :on-cancel #(>e [:routing/navigate [:sources/table]])}]]])
